
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import {
  Activity,
  Check,
  HeartPulse,
  Search,
  Star,
  Stethoscope,
} from "lucide-react";

import { API_BASE_URL } from "../../config/apiConfig";

import ProfessionalPageNav from "../../components/professional/ProfessionalPageNav";
import PageLoader from "../../components/common/PageLoader";

import "../../styles/pages/professional/ProfessionalSpecialties.css";


const SPECIALTIES_API_URL =
  `${API_BASE_URL}/professionals/specialties`;

const getStoredToken = () =>
  localStorage.getItem(
    "zethAuthToken"
  ) ||
  sessionStorage.getItem(
    "zethAuthToken"
  );

const clearStoredAuthentication = () => {
  localStorage.removeItem(
    "zethAuthToken"
  );

  localStorage.removeItem(
    "zethUser"
  );

  sessionStorage.removeItem(
    "zethAuthToken"
  );

  sessionStorage.removeItem(
    "zethUser"
  );
};

const getProfileRecord = (
  profileData
) =>
  profileData.user ||
  profileData.profile ||
  profileData.data ||
  null;

const getProfessionId = (
  profile
) => {
  const professionId = Number(
    profile?.professionId ??
      profile?.profession_id ??
      profile?.profession?.id
  );

  return (
    Number.isInteger(
      professionId
    ) &&
    professionId > 0
  )
    ? professionId
    : null;
};

const getProfessionName = (
  profile
) =>
  String(
    profile?.profession_name ||
      profile?.professionName ||
      profile?.profession?.name ||
      (
        typeof profile?.profession ===
        "string"
          ? profile.profession
          : ""
      )
  ).trim();

const getSpecialtyOptionsUrls = ({
  professionId,
  professionName,
}) => {
  const urls = [];

  if (professionId) {
    urls.push(
      `${SPECIALTIES_API_URL}/options?professionId=${professionId}`
    );
  }

  if (professionName) {
    urls.push(
      `${SPECIALTIES_API_URL}/options?profession=${encodeURIComponent(
        professionName
      )}`
    );
  }

  urls.push(
    `${SPECIALTIES_API_URL}/options`
  );

  return [
    ...new Set(urls),
  ];
};

/*
 * These categories are used only for display.
 * The current backend does not need to change.
 */
const SPECIALTY_CATEGORY_RULES = [
  {
    name: "Critical Care & Emergency",
    keywords: [
      "emergency",
      "intensive care",
      "icu",
      "critical care",
      "coronary care",
      "ccu",
      "trauma",
      "pacu",
      "post anesthesia",
      "recovery room",
    ],
  },
  {
    name: "Maternal, Newborn & Pediatrics",
    keywords: [
      "labour",
      "labor",
      "delivery",
      "obstetric",
      "mother",
      "baby",
      "maternal",
      "newborn",
      "neonatal",
      "nicu",
      "pediatric",
      "paediatric",
    ],
  },
  {
    name: "Medical & Surgical",
    keywords: [
      "medical",
      "medicine",
      "surgical",
      "surgery",
      "med-surg",
      "med surg",
      "telemetry",
      "cardiology",
      "cardiac",
      "oncology",
      "dialysis",
      "nephrology",
      "orthopedic",
      "orthopaedic",
      "neurology",
      "respiratory",
      "gastroenterology",
      "urology",
      "operating room",
      "perioperative",
    ],
  },
  {
    name: "Mental Health & Rehabilitation",
    keywords: [
      "mental health",
      "psychiat",
      "addiction",
      "behavioral",
      "behavioural",
      "rehabilitation",
      "rehab",
      "palliative",
      "hospice",
    ],
  },
  {
    name: "Community & Continuing Care",
    keywords: [
      "long-term care",
      "long term care",
      "ltc",
      "home care",
      "community",
      "public health",
      "primary care",
      "clinic",
      "ambulatory",
      "correctional",
      "remote",
      "rural",
    ],
  },
];

const getSpecialtyCategory = (specialty) => {
  /*
   * Use a backend-provided category when one is available.
   */
  const existingCategory =
    specialty.category ||
    specialty.category_name ||
    specialty.group_name;

  if (existingCategory) {
    return existingCategory;
  }

  const specialtyName =
    String(specialty.name || "").toLowerCase();

  const matchingCategory =
    SPECIALTY_CATEGORY_RULES.find((category) =>
      category.keywords.some((keyword) =>
        specialtyName.includes(keyword)
      )
    );

  return matchingCategory?.name || "Other Specialties";
};

const ProfessionalSpecialties = () => {
  const navigate = useNavigate();

  const [
    profession,
    setProfession,
  ] = useState({
    id: null,
    name: "",
  });
  const [
    availableSpecialties,
    setAvailableSpecialties,
  ] = useState([]);

  const [
    selectedSpecialties,
    setSelectedSpecialties,
  ] = useState([]);

  const [
    primarySpecialty,
    setPrimarySpecialty,
  ] = useState(null);

  const [searchTerm, setSearchTerm] =
    useState("");

  const [loading, setLoading] =
    useState(true);

  const [
    showLoadingIndicator,
    setShowLoadingIndicator,
  ] = useState(false);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  const [
    successMessage,
    setSuccessMessage,
  ] = useState("");

  const getToken = () => {
    const token =
      getStoredToken();

    if (!token) {
      throw new Error(
        "Authentication required. Please sign in again."
      );
    }

    return token;
  };

  const parseResponse = async (
    response
  ) => {
    const contentType =
      response.headers.get(
        "content-type"
      );

    if (
      !contentType?.includes(
        "application/json"
      )
    ) {
      throw new Error(
        "The server returned an invalid response."
      );
    }

    return response.json();
  };

  const loadPage =
    useCallback(async () => {
      try {
        setLoading(true);
        setError("");
        setSuccessMessage("");

        const token = getToken();

        const requestOptions = {
          method: "GET",

          headers: {
            Authorization:
              `Bearer ${token}`,
          },
        };

        const profileResponse =
          await fetch(
            `${API_BASE_URL}/profile`,
            requestOptions
          );

        if (
          profileResponse.status ===
            401 ||
          profileResponse.status ===
            403
        ) {
          clearStoredAuthentication();

          navigate(
            "/professional/login",
            {
              replace: true,
            }
          );

          return;
        }

        const profileData =
          await parseResponse(
            profileResponse
          );

        if (
          !profileResponse.ok ||
          profileData.success === false
        ) {
          throw new Error(
            profileData.message ||
              "Unable to load your profile."
          );
        }

        const profileRecord =
          getProfileRecord(
            profileData
          );

        const professionId =
          getProfessionId(
            profileRecord
          );

        const professionName =
          getProfessionName(
            profileRecord
          );

        if (!professionName) {
          throw new Error(
            "Please select your profession in Personal Information before adding clinical specialties."
          );
        }

        setProfession({
          id: professionId,
          name: professionName,
        });

        const selectedResponse =
          await fetch(
            SPECIALTIES_API_URL,
            requestOptions
          );

        if (
          selectedResponse.status ===
            401 ||
          selectedResponse.status ===
            403
        ) {
          clearStoredAuthentication();

          navigate(
            "/professional/login",
            {
              replace: true,
            }
          );

          return;
        }

        const selectedData =
          await parseResponse(
            selectedResponse
          );

        if (
          !selectedResponse.ok ||
          selectedData.success === false
        ) {
          throw new Error(
            selectedData.message ||
              "Unable to load your selected specialties."
          );
        }

        const optionUrls =
          getSpecialtyOptionsUrls({
            professionId,
            professionName,
          });

        let optionsData = null;
        let optionsError = null;

        for (
          const optionsUrl of
          optionUrls
        ) {
          const optionsResponse =
            await fetch(
              optionsUrl,
              requestOptions
            );

          if (
            optionsResponse.status ===
              401 ||
            optionsResponse.status ===
              403
          ) {
            clearStoredAuthentication();

            navigate(
              "/professional/login",
              {
                replace: true,
              }
            );

            return;
          }

          const candidateData =
            await parseResponse(
              optionsResponse
            );

          if (
            optionsResponse.ok &&
            candidateData.success !== false
          ) {
            optionsData =
              candidateData;

            break;
          }

          optionsError =
            candidateData.message ||
            "Unable to load available specialties.";
        }

        if (!optionsData) {
          throw new Error(
            optionsError ||
              `No specialty catalogue is configured for ${professionName}.`
          );
        }

        const available =
          Array.isArray(
            optionsData.specialties
          )
            ? optionsData.specialties
            : Array.isArray(
                optionsData.data
              )
              ? optionsData.data
              : [];

        const selected =
          Array.isArray(
            selectedData.specialties
          )
            ? selectedData.specialties
            : Array.isArray(
                selectedData.data
              )
              ? selectedData.data
              : [];

        const selectedIds =
          selected
            .map((item) =>
              Number(
                item.specialty_id ??
                  item.specialtyId ??
                  item.id
              )
            )
            .filter(
              (id) =>
                Number.isInteger(id) &&
                id > 0
            );

        const primary =
          selected.find(
            (item) =>
              item.is_primary ===
                true ||
              item.is_primary ===
                1 ||
              item.is_primary ===
                "1" ||
              item.isPrimary ===
                true ||
              item.isPrimary ===
                1 ||
              item.primary ===
                true ||
              item.primary ===
                1
          );

        setAvailableSpecialties(
          available
        );

        setSelectedSpecialties(
          selectedIds
        );

        setPrimarySpecialty(
          primary
            ? Number(
                primary.specialty_id ??
                  primary.specialtyId ??
                  primary.id
              )
            : null
        );
      } catch (loadError) {
        console.error(
          "Load specialties page error:",
          loadError
        );

        setError(
          loadError.message ||
            "Unable to load clinical specialties."
        );
      } finally {
        setLoading(false);
      }
    }, [navigate]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    if (
      primarySpecialty !== null &&
      !selectedSpecialties.includes(
        primarySpecialty
      )
    ) {
      setPrimarySpecialty(null);
    }
  }, [
    selectedSpecialties,
    primarySpecialty,
  ]);

  useEffect(() => {
    if (!loading) {
      setShowLoadingIndicator(false);
      return undefined;
    }

    const timer =
      window.setTimeout(() => {
        setShowLoadingIndicator(true);
      }, 300);

    return () => {
      window.clearTimeout(timer);
    };
  }, [loading]);

  const handleSpecialtyChange = (
    specialtyId
  ) => {
    setError("");
    setSuccessMessage("");

    setSelectedSpecialties(
      (currentSpecialties) => {
        if (
          currentSpecialties.includes(
            specialtyId
          )
        ) {
          return currentSpecialties.filter(
            (id) =>
              id !== specialtyId
          );
        }

        return [
          ...currentSpecialties,
          specialtyId,
        ];
      }
    );
  };

  const handlePrimaryChange = (
    specialtyId
  ) => {
    setError("");
    setSuccessMessage("");

    /*
     * Automatically select the specialty
     * before making it primary.
     */
    setSelectedSpecialties(
      (currentSpecialties) =>
        currentSpecialties.includes(
          specialtyId
        )
          ? currentSpecialties
          : [
              ...currentSpecialties,
              specialtyId,
            ]
    );

    setPrimarySpecialty(
      specialtyId
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccessMessage("");

      const token = getToken();

      if (
        selectedSpecialties.length ===
        0
      ) {
        throw new Error(
          "Please select at least one clinical specialty."
        );
      }

      if (
        primarySpecialty === null
      ) {
        throw new Error(
          "Please choose one selected specialty as your primary specialty."
        );
      }

      if (
        !selectedSpecialties.includes(
          primarySpecialty
        )
      ) {
        throw new Error(
          "The primary specialty must be one of your selected specialties."
        );
      }

      const response = await fetch(
        SPECIALTIES_API_URL,
        {
          method: "PUT",

          headers: {
            "Content-Type":
              "application/json",

            Authorization:
              `Bearer ${token}`,
          },

          body: JSON.stringify({
            specialtyIds:
              selectedSpecialties,

            primarySpecialtyId:
              primarySpecialty,
          }),
        }
      );

      const data =
        await parseResponse(response);

      if (
        !response.ok ||
        !data.success
      ) {
        throw new Error(
          data.message ||
            "Unable to save clinical specialties."
        );
      }

      setSuccessMessage(
        data.message ||
          "Clinical specialties updated successfully."
      );
    } catch (saveError) {
      console.error(
        "Save specialties error:",
        saveError
      );

      setError(
        saveError.message ||
          "Unable to save clinical specialties."
      );
    } finally {
      setSaving(false);
    }
  };

  const primarySpecialtyRecord =
    useMemo(
      () =>
        availableSpecialties.find(
          (specialty) =>
            Number(specialty.id) ===
            primarySpecialty
        ) || null,
      [
        availableSpecialties,
        primarySpecialty,
      ]
    );

  const additionalSpecialtyCount =
    Math.max(
      selectedSpecialties.length -
        (primarySpecialty ? 1 : 0),
      0
    );

  const profileStrength =
    useMemo(() => {
      if (
        selectedSpecialties.length ===
        0
      ) {
        return "Not Started";
      }

      if (
        primarySpecialty === null
      ) {
        return "In Progress";
      }

      return "Complete";
    }, [
      selectedSpecialties,
      primarySpecialty,
    ]);

  const groupedSpecialties =
    useMemo(() => {
      const normalizedSearch =
        searchTerm
          .trim()
          .toLowerCase();

      const filteredSpecialties =
        availableSpecialties.filter(
          (specialty) => {
            if (!normalizedSearch) {
              return true;
            }

            return String(
              specialty.name || ""
            )
              .toLowerCase()
              .includes(
                normalizedSearch
              );
          }
        );

      return filteredSpecialties.reduce(
        (groups, specialty) => {
          const category =
            getSpecialtyCategory(
              specialty
            );

          if (!groups[category]) {
            groups[category] = [];
          }

          groups[category].push(
            specialty
          );

          return groups;
        },
        {}
      );
    }, [
      availableSpecialties,
      searchTerm,
    ]);

  const orderedCategories =
    useMemo(() => {
      const preferredOrder = [
        "Critical Care & Emergency",
        "Maternal, Newborn & Pediatrics",
        "Medical & Surgical",
        "Mental Health & Rehabilitation",
        "Community & Continuing Care",
        "Other Specialties",
      ];

      const currentCategories =
        Object.keys(
          groupedSpecialties
        );

      return [
        ...preferredOrder.filter(
          (category) =>
            currentCategories.includes(
              category
            )
        ),

        ...currentCategories.filter(
          (category) =>
            !preferredOrder.includes(
              category
            )
        ),
      ];
    }, [groupedSpecialties]);

  if (
    loading &&
    showLoadingIndicator
  ) {
    return (
      <PageLoader
        title="Clinical Specialties"
        message="Loading clinical specialties..."
      />
    );
  }

  if (loading) {
    return null;
  }

  return (
    <main className="professional-specialties">
      <ProfessionalPageNav />

      <header className="specialties-page-header">
        <div>
          <p className="account-label">
            Professional Profile
          </p>

          <h1>
            Clinical Specialties
          </h1>

          <p className="specialties-page-description">
            Tell facilities where you have professional
            clinical experience. Select one Primary
            Specialty and any additional specialties you
            actively practise.
          </p>
        </div>
      </header>

      <section
        className="specialties-summary-grid"
        aria-label="Clinical specialty summary"
      >
        <article className="specialty-summary-card">
          <div className="specialty-summary-icon specialty-summary-icon-primary">
            <Star
              size={21}
              strokeWidth={2}
            />
          </div>

          <div className="specialty-summary-content">
            <span className="specialty-summary-label">
              Primary Specialty
            </span>

            <strong className="specialty-summary-text">
              {primarySpecialtyRecord?.name ||
                "Not selected"}
            </strong>

            <small>
              Main clinical area
            </small>
          </div>
        </article>

        <article className="specialty-summary-card">
          <div className="specialty-summary-icon specialty-summary-icon-blue">
            <Stethoscope
              size={21}
              strokeWidth={2}
            />
          </div>

          <div className="specialty-summary-content">
            <span className="specialty-summary-label">
              Additional Specialties
            </span>

            <strong className="specialty-summary-number">
              {additionalSpecialtyCount}
            </strong>

            <small>
              Secondary clinical areas
            </small>
          </div>
        </article>

        <article className="specialty-summary-card">
          <div className="specialty-summary-icon specialty-summary-icon-green">
            <HeartPulse
              size={21}
              strokeWidth={2}
            />
          </div>

          <div className="specialty-summary-content">
            <span className="specialty-summary-label">
              Selected Specialties
            </span>

            <strong className="specialty-summary-number">
              {selectedSpecialties.length}
            </strong>

            <small>
              Experience profiles selected
            </small>
          </div>
        </article>

        <article className="specialty-summary-card">
          <div className="specialty-summary-icon specialty-summary-icon-purple">
            <Activity
              size={21}
              strokeWidth={2}
            />
          </div>

          <div className="specialty-summary-content">
            <span className="specialty-summary-label">
              Profile Strength
            </span>

            <strong className="specialty-summary-text">
              {profileStrength}
            </strong>

            <small>
              {profileStrength === "Complete"
                ? "Primary specialty selected"
                : "Select specialties and mark one primary"}
            </small>
          </div>
        </article>
      </section>

      {error && (
        <div
          className="specialties-error"
          role="alert"
        >
          {error}
        </div>
      )}

      {successMessage && (
        <div
          className="specialties-message"
          role="status"
        >
          {successMessage}
        </div>
      )}

      <section className="specialties-card">
        <div className="specialties-card-header">
          <div>
            <h2>
              Select Clinical Specialties
            </h2>

            <p className="specialties-card-description">
              Select every specialty in which you have
              professional experience. Mark one selected
              specialty as your primary clinical area.
            </p>
          </div>

          <span className="specialties-selected-count">
            {selectedSpecialties.length} selected
          </span>
        </div>

        <div className="specialties-search-wrapper">
          <Search
            size={19}
            strokeWidth={2}
            aria-hidden="true"
          />

          <input
            type="search"
            value={searchTerm}
            onChange={(event) =>
              setSearchTerm(
                event.target.value
              )
            }
            placeholder="Search specialties..."
            aria-label="Search clinical specialties"
          />
        </div>

        {availableSpecialties.length === 0 ? (
          <p className="specialties-empty-message">
            No specialties are currently available for
            your profession.
          </p>
        ) : orderedCategories.length === 0 ? (
          <p className="specialties-empty-message">
            No specialties match your search.
          </p>
        ) : (
          <div className="specialty-category-list">
            {orderedCategories.map(
              (category) => (
                <section
                  className="specialty-category"
                  key={category}
                >
                  <div className="specialty-category-heading">
                    <h3>
                      {category}
                    </h3>

                    <span>
                      {
                        groupedSpecialties[
                          category
                        ].length
                      }
                    </span>
                  </div>

                  <div className="specialties-options">
                    {groupedSpecialties[
                      category
                    ].map(
                      (specialty) => {
                        const specialtyId =
                          Number(
                            specialty.id
                          );

                        const isSelected =
                          selectedSpecialties.includes(
                            specialtyId
                          );

                        const isPrimary =
                          primarySpecialty ===
                          specialtyId;

                        return (
                          <article
                            key={
                              specialtyId
                            }
                            className={[
                              "specialty-option-card",
                              isSelected
                                ? "specialty-option-card-selected"
                                : "",
                              isPrimary
                                ? "specialty-option-card-primary"
                                : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          >
                            <button
                              type="button"
                              className="specialty-selection-button"
                              onClick={() =>
                                handleSpecialtyChange(
                                  specialtyId
                                )
                              }
                              aria-pressed={
                                isSelected
                              }
                            >
                              <span className="specialty-option-name">
                                {
                                  specialty.name
                                }
                              </span>

                              <span
                                className="specialty-selection-indicator"
                                aria-hidden="true"
                              >
                                {isSelected ? (
                                  <Check
                                    size={
                                      17
                                    }
                                    strokeWidth={
                                      3
                                    }
                                  />
                                ) : (
                                  "+"
                                )}
                              </span>
                            </button>

                            {isSelected && (
                              <div className="specialty-card-footer">
                                <span className="specialty-selected-label">
                                  <Check
                                    size={15}
                                    strokeWidth={
                                      2.5
                                    }
                                  />

                                  Selected
                                </span>

                                {isPrimary ? (
                                  <span className="specialty-primary-badge">
                                    <Star
                                      size={
                                        14
                                      }
                                      strokeWidth={
                                        2.5
                                      }
                                      fill="currentColor"
                                    />

                                    Primary
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    className="make-primary-button"
                                    onClick={() =>
                                      handlePrimaryChange(
                                        specialtyId
                                      )
                                    }
                                  >
                                    <Star
                                      size={
                                        14
                                      }
                                      strokeWidth={
                                        2
                                      }
                                    />

                                    Make Primary
                                  </button>
                                )}
                              </div>
                            )}
                          </article>
                        );
                      }
                    )}
                  </div>
                </section>
              )
            )}
          </div>
        )}
      </section>

      <div className="specialties-save-actions">
        <button
          type="button"
          className="specialties-save-button"
          onClick={handleSave}
          disabled={
            saving ||
            availableSpecialties.length ===
              0 ||
            !profession.name
          }
        >
          {saving
            ? "Saving..."
            : "Save Specialties"}
        </button>
      </div>
    </main>
  );
};

export default ProfessionalSpecialties;
